import { EV_IDENTIFICATION_LABELS, type EvOverview, type EvOverviewConnector } from "@station/contracts";
import { Alert, Button, Card, EmptyState, MoneyText, SkeletonRows } from "@station/ui";
import { createFileRoute, Link } from "@tanstack/react-router";
import { EvCharger, Import, Play, Square } from "lucide-react";
import { type ReactElement, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { type ChargerRef, RemoteStartDialog } from "../../../features/ev/charger-dialogs.js";
import {
  formatKw,
  formatKwh,
  formatPercent,
  formatWatts,
  isSessionLive,
  secondsBetween,
} from "../../../features/ev/ev-values.js";
import { useEvOverview } from "../../../features/ev/queries.js";
import { StopSessionDialog } from "../../../features/ev/session-actions.js";
import {
  ConnectorStatusBadge,
  evSearchShape,
  EvStationPage,
  type EvStationContext,
  Field,
  OnlineBadge,
  SessionPhaseBadge,
} from "../../../features/ev/shared.js";
import { formatDuration } from "../../../features/wash/wash-values.js";
import { errorMessage } from "../../../lib/errors.js";
import { formatCount, formatDateTime } from "../../../lib/format.js";

const searchSchema = z.object(evSearchShape);

export const Route = createFileRoute("/_app/sarj/pano")({
  validateSearch: (search) => searchSchema.parse(search),
  component: DashboardRoute,
});

/**
 * Şarj canlı panosu (Prompt 19 web, `GET /ev/overview`): konnektör ızgarası — ünite · konnektör, tür · güç, METİNLİ durum
 * rozeti (son bilinen OCPP durumu; arızada hata kodu), çevrimiçi / çevrimdışı, canlı oturumda kWh · kW · süre · SoC ve yük
 * yönetiminin sınırı — ve bugünün özeti (oturum, kWh, tutar, ödenmemiş). `ev.*` olayları (istasyon odası) panoyu tazeler;
 * ayrıca 30 sn'de bir yoklanır, süreler 15 sn'de bir akar. Uzaktan başlatma / durdurma `ev.operate` ister.
 */
function DashboardRoute(): ReactElement {
  const { t } = useTranslation();
  return (
    <EvStationPage title={t("menu.evDashboard")} description={t("ev.dashboard.description")}>
      {(context) => <DashboardBody context={context} />}
    </EvStationPage>
  );
}

/** Süreler her 15 sn'de bir yeniden hesaplanır (tuval yerine metin; ekran okuyucuyu boğmaz). */
function useNow(intervalMs = 15_000): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const handle = setInterval(() => setNow(Date.now()), intervalMs);
    return () => clearInterval(handle);
  }, [intervalMs]);
  return now;
}

function DashboardBody({ context }: { context: EvStationContext }): ReactElement {
  const { t } = useTranslation();
  const overview = useEvOverview(context.station.id);
  const now = useNow();
  const [dialog, setDialog] = useState<
    | { kind: "start"; charger: ChargerRef; connectorNo: number }
    | { kind: "stop"; connector: EvOverviewConnector }
    | null
  >(null);

  if (overview.error) return <Alert tone="danger">{errorMessage(overview.error)}</Alert>;
  if (!overview.data) return <SkeletonRows rows={6} />;
  const data: EvOverview = overview.data;
  const partner = data.operationModel === "NETWORK_PARTNER";
  const canOperate = context.permissions.canOperate && !partner;
  const chargerRef = (connector: EvOverviewConnector): ChargerRef => ({
    id: connector.chargerId,
    name: connector.chargerName,
    ocppIdentity: null,
    connectors: data.connectors
      .filter((item) => item.chargerId === connector.chargerId)
      .map((item) => ({
        connectorNo: item.connectorNo,
        connectorType: item.connectorType,
        powerKw: item.powerKw,
        ocppStatus: item.status,
      })),
  });

  return (
    <>
      {partner ? (
        <Alert tone="info" title={t("ev.dashboard.partnerTitle")}>
          <span className="grid gap-1">
            {t("ev.dashboard.partnerHint")}
            <Link
              to="/sarj/ag-aktarimlari"
              className="inline-flex items-center gap-1 underline underline-offset-4"
            >
              <Import className="size-4" aria-hidden="true" />
              {t("menu.evPartnerImports")}
            </Link>
          </span>
        </Alert>
      ) : null}
      <Card className="grid gap-3 p-4" data-testid="ev-today-summary">
        <div className="flex flex-wrap items-baseline justify-between gap-2">
          <h2 className="text-base font-semibold">{t("ev.dashboard.todayTitle")}</h2>
          <span className="text-xs text-muted-foreground">
            {t("ev.dashboard.generatedAt", { at: formatDateTime(data.generatedAt) })}
          </span>
        </div>
        <dl className="grid grid-cols-2 items-start gap-x-6 gap-y-3 sm:grid-cols-4 xl:grid-cols-8">
          <Field label={t("ev.dashboard.sessions")}>
            <span className="tabular text-lg">{formatCount(data.today.sessionCount)}</span>
          </Field>
          <Field label={t("ev.dashboard.live")}>
            <span className="tabular text-lg">{formatCount(data.today.liveCount)}</span>
          </Field>
          <Field label={t("ev.dashboard.completed")}>
            <span className="tabular text-lg">{formatCount(data.today.completedCount)}</span>
          </Field>
          <Field label={t("ev.dashboard.energy")}>
            <span className="tabular text-lg">{formatKwh(data.today.energyKwh)}</span>
          </Field>
          <Field label={t("ev.dashboard.amount")}>
            <MoneyText value={data.today.totalAmount} className="text-lg" />
          </Field>
          <Field label={t("ev.dashboard.unpaid")}>
            <span className="grid">
              <MoneyText value={data.today.unpaidAmount} className="text-lg" />
              <span className="text-xs text-muted-foreground">
                {t("ev.dashboard.unpaidCount", { count: data.today.unpaidCount })}
              </span>
            </span>
          </Field>
          <Field label={t("ev.dashboard.faulted")}>
            <span className={`tabular text-lg ${data.today.faultedConnectors > 0 ? "text-destructive" : ""}`}>
              {formatCount(data.today.faultedConnectors)}
            </span>
          </Field>
          <Field label={t("ev.dashboard.offline")}>
            <span className="tabular text-lg">{formatCount(data.today.offlineChargers)}</span>
          </Field>
        </dl>
        {data.today.unpaidCount > 0 ? (
          <Link
            to="/sarj/oturumlar"
            search={{ station: context.station.id, paymentStatus: "UNPAID", phase: "COMPLETED" } as never}
            className="text-sm underline underline-offset-4"
          >
            {t("ev.dashboard.openUnpaid")}
          </Link>
        ) : null}
      </Card>

      {data.connectors.length === 0 ? (
        <EmptyState
          headingLevel={2}
          icon={<EvCharger />}
          title={t("ev.dashboard.emptyTitle")}
          description={t("ev.dashboard.emptyDescription")}
        />
      ) : (
        <ul
          className="grid gap-4 md:grid-cols-2 xl:grid-cols-3"
          aria-label={t("ev.dashboard.gridLabel")}
          data-testid="ev-connector-grid"
        >
          {data.connectors.map((connector) => (
            <li key={`${connector.chargerId}-${connector.connectorNo}`}>
              <ConnectorCard
                connector={connector}
                stationId={context.station.id}
                now={now}
                canOperate={canOperate}
                onStart={() =>
                  setDialog({
                    kind: "start",
                    charger: chargerRef(connector),
                    connectorNo: connector.connectorNo,
                  })
                }
                onStop={() => setDialog({ kind: "stop", connector })}
              />
            </li>
          ))}
        </ul>
      )}

      {dialog?.kind === "start" ? (
        <RemoteStartDialog
          open
          onOpenChange={(open) => !open && setDialog(null)}
          charger={dialog.charger}
          defaultConnectorNo={dialog.connectorNo}
        />
      ) : null}
      {dialog?.kind === "stop" && dialog.connector.session ? (
        <StopSessionDialog
          open
          onOpenChange={(open) => !open && setDialog(null)}
          session={{
            id: dialog.connector.session.sessionId,
            chargerName: dialog.connector.chargerName,
            connectorNo: dialog.connector.connectorNo,
            transactionId: null,
            energyKwh: dialog.connector.session.energyKwh,
            phase: dialog.connector.session.phase,
          }}
        />
      ) : null}
    </>
  );
}

function ConnectorCard({
  connector,
  stationId,
  now,
  canOperate,
  onStart,
  onStop,
}: {
  connector: EvOverviewConnector;
  stationId: string;
  now: number;
  canOperate: boolean;
  onStart: () => void;
  onStop: () => void;
}): ReactElement {
  const { t } = useTranslation();
  const headingId = `connector-${connector.chargerId}-${connector.connectorNo}`;
  const session = connector.session;
  const startable =
    canOperate &&
    connector.online &&
    session === null &&
    (connector.status === "Available" || connector.status === "Preparing");
  const stoppable =
    canOperate && session !== null && isSessionLive(session.phase) && session.phase !== "FINISHING";
  const label = `${connector.chargerName} · ${t("ev.connector.no", { no: connector.connectorNo })}`;
  const tone = connector.faulted ? "border-destructive/60" : session !== null ? "border-info/60" : "";

  return (
    <Card
      className={`grid h-full gap-3 p-4 ${tone}`}
      aria-labelledby={headingId}
      data-testid="ev-connector-card"
    >
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="grid gap-0.5">
          <h2 id={headingId} className="text-base font-semibold">
            {label}
          </h2>
          <span className="text-xs text-muted-foreground">
            {t(`enums.connectorType.${connector.connectorType}`)} · {formatKw(connector.powerKw)}
          </span>
        </div>
        <div className="flex flex-wrap items-center gap-1.5">
          <ConnectorStatusBadge status={connector.status} errorCode={connector.errorCode} />
          <OnlineBadge online={connector.online} />
        </div>
      </div>
      {connector.statusSince ? (
        <p className="text-xs text-muted-foreground">
          {connector.online
            ? t("ev.connector.since", { at: formatDateTime(connector.statusSince) })
            : t("ev.connector.lastKnown", { at: formatDateTime(connector.statusSince) })}
        </p>
      ) : null}
      {connector.faulted && connector.errorCode ? (
        <p className="text-sm font-medium text-destructive">
          {t("ev.connector.errorCode", { code: connector.errorCode })}
        </p>
      ) : null}

      {session ? (
        <div className="grid gap-2 rounded-md bg-info-subtle px-3 py-2 text-sm" data-testid="ev-live-session">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <SessionPhaseBadge phase={session.phase} />
            <span className="text-xs">{EV_IDENTIFICATION_LABELS[session.identification]}</span>
          </div>
          <dl className="grid grid-cols-2 gap-x-4 gap-y-1.5">
            <Field label={t("ev.fields.energy")}>
              <span className="tabular">{formatKwh(session.energyKwh)}</span>
            </Field>
            <Field label={t("ev.fields.power")}>
              <span className="tabular">{formatWatts(session.powerW)}</span>
            </Field>
            <Field label={t("ev.fields.duration")}>
              <span className="tabular">{formatDuration(secondsBetween(session.startedAt, null, now))}</span>
            </Field>
            <Field label={t("ev.fields.soc")}>
              <span className="tabular">{formatPercent(session.socPercent, 0)}</span>
            </Field>
            <Field className="col-span-2" label={t("ev.fields.limit")}>
              <span className="tabular">
                {session.currentLimitW === null ? t("ev.load.unlimited") : formatWatts(session.currentLimitW)}
              </span>
            </Field>
          </dl>
          <Link
            to="/sarj/oturumlar/$id"
            params={{ id: session.sessionId }}
            search={{ station: stationId } as never}
            className="text-xs underline underline-offset-4"
            aria-label={t("ev.dashboard.openSessionFor", { name: label })}
          >
            {t("ev.dashboard.openSession")}
          </Link>
        </div>
      ) : null}

      {startable || stoppable ? (
        <div className="mt-auto flex flex-wrap gap-2 border-t pt-3">
          {startable ? (
            <Button size="sm" onClick={onStart} aria-label={t("ev.remoteStart.openFor", { name: label })}>
              <Play aria-hidden="true" />
              {t("ev.remoteStart.open")}
            </Button>
          ) : null}
          {stoppable ? (
            <Button
              size="sm"
              variant="outline"
              onClick={onStop}
              aria-label={t("ev.sessionActions.stopFor", { name: label })}
            >
              <Square aria-hidden="true" />
              {t("ev.sessionActions.stop")}
            </Button>
          ) : null}
        </div>
      ) : null}
    </Card>
  );
}
